import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { usePublicConfig, publicConfigUrl } from "../../lib/usePublicConfig";
import TopStrip from "./TopStrip";

const links = [
  { to: "/hoe-werkt-het", label: "Hoe werkt het" },
  { to: "/prijzen", label: "Prijzen" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog" },
  { to: "/voor-bedrijven", label: "Voor bedrijven" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const cfg = usePublicConfig();
  const logoUrl = publicConfigUrl(cfg.logo_header_url);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="oc-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled
          ? "shadow-[0_2px_24px_rgba(15,39,68,0.08)]"
          : ""
      }`}
    >
      <TopStrip />
      <div className={`bg-white/95 backdrop-blur-xl border-b ${
        scrolled ? "border-oc-border" : "border-transparent"
      }`}>
        <div className="oc-container flex items-center justify-between h-[72px]">
        <Link
          to="/"
          data-testid="oc-logo-link"
          className="flex items-center gap-2 font-display font-bold text-[20px] tracking-tight text-oc-primary"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="OfferteExpert" className="h-9 w-auto max-w-[200px]" />
          ) : (
            <>
              <ShieldCheck className="w-7 h-7 text-oc-secondary" strokeWidth={2.2} />
              <span className="text-oc-primary">OfferteExpert</span>
            </>
          )}
        </Link>        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-[15px] font-medium transition-colors ${
                  isActive ? "text-oc-primary" : "text-oc-textSecondary hover:text-oc-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/analyse" className="hidden sm:block">
            <Button
              data-testid="nav-cta-analyseer"
              className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn px-5 h-11 font-semibold"
            >
              Analyseer mijn offerte
            </Button>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden rounded-md p-2 text-oc-primary"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-oc-border">
          <div className="oc-container py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="text-oc-primary text-base font-medium py-1"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/analyse">
              <Button className="bg-oc-primary hover:bg-oc-secondary text-white w-full h-12 rounded-oc-btn font-semibold">
                Analyseer mijn offerte
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
